import * as hardware from "@client/hardware";
import * as utils from "@client/utils";
import {server} from "@reactivated";

// The system share sheet for a published share. Safari only opens it inside
// a user gesture, and the activation survives the short publish await that
// precedes it; a dismissed sheet rejects with AbortError, which is not news.
export const shareLink = async (share_uuid: string, title: string) => {
    const url = `${utils.getBaseURLWithOriginForCapacitor()}/share/${share_uuid}/`;
    if (typeof navigator.share !== "function") {
        await navigator.clipboard.writeText(url);
        return "copied" as const;
    }
    try {
        await navigator.share({title, url});
        void hardware.haptic();
        return "shared" as const;
    } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            return "canceled" as const;
        }
        throw error;
    }
};

const MINT_TIMEOUT_MS = 3000;

// Safari drops the clipboard gesture on any await before the write, so the
// write starts at once and the text is a promise the clipboard resolves.
export const copyTrackedCalculatorLink = async (
    url: string,
    source: server.core.rpc.CreateCalculatorShare["source"],
) => {
    const prepared = Promise.race([
        server.core.rpc.create_calculator_share({source}),
        new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("mint timed out")), MINT_TIMEOUT_MS),
        ),
    ])
        .then((result) => {
            if (result.type !== "success" || result.data == null) {
                return {url, code: null};
            }
            const tracked = new URL(url);
            tracked.searchParams.set("t", result.data);
            return {url: tracked.href, code: result.data};
        })
        .catch(() => ({url, code: null}));
    if (typeof ClipboardItem === "undefined") {
        const result = await prepared;
        await navigator.clipboard.writeText(result.url);
        return result.code;
    }
    await navigator.clipboard.write([
        new ClipboardItem({
            "text/plain": prepared.then(
                (result) => new Blob([result.url], {type: "text/plain"}),
            ),
        }),
    ]);
    return (await prepared).code;
};
